import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING, VIEW, ADD, EDIT, OPEN,
} from "const"
import { OkrKeyResult } from "server/models"
import { StateType, hasAccess } from "client/utils"
import { okrKeyResultsRoute } from "client/parts/routes"
import {
    PRM_OKR_OBJECTIVE_KEY_RESULT,
    StoreType,
    OkrObjectiveObjectProps,
    OkrKeyResultUpdateList,

    LoadOkrObjectiveKeyResults,
    LoadOkrKeyResultUpdates,
    DragnDropOkrKeyResult,
    IsOwnerOkrObjective,

    setOkrKeyResultList,
    setOkrKeyResultIds,
    setOkrKeyResultActive,
    setOkrKeyResultUpdateList,
    addModal,
} from "client/store"
import {
    PageContent,
    Loader,
    Button,
    Table,
    DragnDrop,
} from "client/components"

import { KeyResultWrapper, ProgressPercents } from "client/pages/okr/styled"
import { ListTitleWrapper, ListTitle, ListProgress } from "../styled"

import Columns from "./columns"
import PopupMenu from "./popupMenu"
import ConfirmDelete from "./confirmDelete"

const KeyResults = ({
    objective,
}: OkrObjectiveObjectProps) => {
    const session = useSelector((state: StoreType) => state.okrSession.sessionView)

    const objectiveBaseRoute = useSelector((state: StoreType) => state.okrObjective.baseRoute)
    const objectiveRoute = useSelector((state: StoreType) => state.okrObjective.objectiveRoute)

    const keyResultList = useSelector((state: StoreType) => state.okrKeyResult.keyResultList)
    const keyResultIds = useSelector((state: StoreType) => state.okrKeyResult.keyResultIds)
    const keyResultUpdateList = useSelector((state: StoreType) => state.okrKeyResultUpdate.updateList)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [progress, setProgress] = useState(0)

    const LoadData = () => {
        setLoadState(PENDING)
        LoadOkrObjectiveKeyResults(objective.id, session.id).then((res) => {
            dispatch(setOkrKeyResultList({
                ...keyResultList,
                ...res.keyResultList,
            }))
            dispatch(setOkrKeyResultIds({
                ...keyResultIds,
                [objective.id]: res.keyResultIds,
            }))

            const promiseArray: Promise<any>[] = []
            const updateList: OkrKeyResultUpdateList = {}

            Object.values(res.keyResultList).forEach((keyResult: OkrKeyResult) => {
                promiseArray.push(
                    LoadOkrKeyResultUpdates(keyResult).then((updates) => {
                        updateList[keyResult.id] = updates
                    }),
                )
            })
            Promise.all(promiseArray).then(() => {
                dispatch(setOkrKeyResultUpdateList(updateList))
                setLoadState(IDLE)
            })
        })
    }

    useEffect(() => {
        // don't use "reloadList" here. After updating details all data will be reloaded from the main objective list
        LoadData()

        return () => {
            // clear popup menu object
            dispatch(setOkrKeyResultActive(null))
        }
    }, [])

    useEffect(() => {
        const krIds = keyResultIds[objective.id] || []
        let krProgress = 0

        krIds.forEach((id) => {
            const krUpdates = keyResultUpdateList[id]
            const lastUpdate = krUpdates && krUpdates.length > 0 ? krUpdates[krUpdates.length - 1] : null
            if (lastUpdate) krProgress += lastUpdate.progress
        })

        if (krIds.length > 0) krProgress = Math.round(krProgress / krIds.length)
        else krProgress = 0

        setProgress(krProgress)
    }, [objective, keyResultIds, keyResultUpdateList])

    const onDragnDrop: DragnDrop = (dataIds, callback) => {
        dispatch(setOkrKeyResultIds({
            ...keyResultIds,
            [objective.id]: dataIds,
        }))
        setLoadState(PENDING)
        DragnDropOkrKeyResult(dataIds, keyResultList).then((success) => {
            LoadData()
            callback(success)
        })
    }

    const baseRoute = `${objectiveBaseRoute}${objectiveRoute}/${VIEW}/${objective.id}${okrKeyResultsRoute}`
    const columns = Columns(baseRoute, objective)

    const isOwnerObjective = IsOwnerOkrObjective(objective, userData.userId)
    const isOwnerSession = session.owner === userData.userId
    const isOwner = isOwnerObjective || isOwnerSession

    const isOpenSession = session.status === OPEN

    const addAccess = hasAccess(PRM_OKR_OBJECTIVE_KEY_RESULT, ADD)
    const allowedToAdd = (addAccess || isOwner) && isOpenSession

    return (
        <KeyResultWrapper>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <ListTitleWrapper>
                    <ListTitle>{lang.progress}</ListTitle>
                    <ListProgress>
                        <ProgressPercents percents={progress}>{`${progress}%`}</ProgressPercents>
                    </ListProgress>
                </ListTitleWrapper>
                <Table
                    columns={columns}
                    dataList={keyResultList}
                    dataIds={keyResultIds[objective.id] || []}
                    rowSize="fit"
                    onDragnDrop={onDragnDrop}
                    bottomContent={allowedToAdd && (
                        <Button
                            variant="success"
                            onClick={() => dispatch(addModal({
                                url: `${baseRoute}/${EDIT}/0`,
                            }))}
                        >
                            {lang.add}
                        </Button>
                    )}
                />

                <PopupMenu />
                <ConfirmDelete />
            </PageContent>
        </KeyResultWrapper>
    )
}
export default KeyResults
